/**
 * Chess Accuracy Calculator
 * Calculates accuracy scores for both players based on Stockfish evaluations
 * Uses the Lichess accuracy calculation algorithm
 * adapted from:
 * https://github.com/lichess-org/lila/blob/master/modules/analyse/src/main/AccuracyPercent.scala
 */
// Lichess constants
const WIN_PERCENT_MULTIPLIER = -0.00368208;
const ACCURACY_COEFF = 103.1668100711649;
const ACCURACY_EXP_FACTOR = -0.04354415386753951;
const ACCURACY_OFFSET = -3.166924740191411;
const UNCERTAINTY_BONUS = 1.0;
const MIN_VOLATILITY = 0.5;
const MAX_VOLATILITY = 12;
const CENTIPAWN_CAP = 1000;
const MATE_EQUIVALENT = 1000;
/**
 * Convert centipawns to win percentage
 */
function centipawnsToWinPercent(centipawns) {
    const cappedCp = Math.max(-CENTIPAWN_CAP, Math.min(CENTIPAWN_CAP, centipawns));
    const winningChance = 2 / (1 + Math.exp(WIN_PERCENT_MULTIPLIER * cappedCp)) - 1;
    return 50 + 50 * winningChance;
}
/**
 * Calculate move accuracy from win percentages
 */
function calculateMoveAccuracy(winPercentBefore, winPercentAfter) {
    if (winPercentAfter >= winPercentBefore) {
        return 100.0; // Perfect move
    }
    const winDiff = winPercentBefore - winPercentAfter;
    const raw = ACCURACY_COEFF * Math.exp(ACCURACY_EXP_FACTOR * winDiff) + ACCURACY_OFFSET;
    const withBonus = raw + UNCERTAINTY_BONUS;
    return Math.max(0, Math.min(100, withBonus));
}
/**
 * Calculate volatilities from win percentages
 */
function calculateVolatilitiesFromWinPercents(allWinPercents, windowSize) {
    const firstWindowRepeats = Math.max(0, Math.min(windowSize, allWinPercents.length) - 2);
    const firstWindow = allWinPercents.slice(0, windowSize);
    const initialWindows = Array(firstWindowRepeats).fill(firstWindow);
    const slidingWindows = [];
    for (let i = 0; i <= allWinPercents.length - windowSize; i++) {
        slidingWindows.push(allWinPercents.slice(i, i + windowSize));
    }
    const allWindows = [...initialWindows, ...slidingWindows];
    const volatilities = allWindows.map((window) => {
        const mean = window.reduce((a, b) => a + b, 0) / window.length;
        const variance = window.reduce((sum, wp) => sum + Math.pow(wp - mean, 2), 0) /
            window.length;
        const stdDev = Math.sqrt(variance);
        return Math.max(MIN_VOLATILITY, Math.min(MAX_VOLATILITY, stdDev));
    });
    return volatilities;
}
/**
 * Calculate color accuracy using weighted and harmonic means
 */
function calculateColorAccuracy(colorAccuracies) {
    if (colorAccuracies.length === 0)
        return 0;
    const weightedSum = colorAccuracies.reduce((sum, ca) => sum + ca.accuracy * ca.weight, 0);
    const totalWeight = colorAccuracies.reduce((sum, ca) => sum + ca.weight, 0);
    const weightedMean = weightedSum / totalWeight;
    const harmonicSum = colorAccuracies.reduce((sum, ca) => sum + 1 / Math.max(0.01, ca.accuracy), 0);
    const harmonicMean = colorAccuracies.length / harmonicSum;
    const finalAccuracy = (weightedMean + harmonicMean) / 2;
    return finalAccuracy;
}
/**
 * Calculate accuracy scores for both players using Lichess-style algorithm
 * @param allCentipawns Comma-separated string of centipawn evaluations
 * @returns Object containing accuracy scores for both players
 */
export function calculateAccuracyScores(allCentipawns) {
    try {
        // Parse the centipawn evaluations
        const evaluations = allCentipawns.split(",").map((evaluation) => {
            const trimmed = evaluation.trim();
            if (trimmed.startsWith("#")) {
                return trimmed; // Keep mate scores as strings
            }
            const num = parseInt(trimmed);
            if (isNaN(num)) {
                throw new Error(`Invalid evaluation: ${trimmed}`);
            }
            return num;
        });
        if (!evaluations.length) {
            return {
                player1Accuracy: 0,
                player2Accuracy: 0,
            };
        }
        // Convert evaluations to centipawns, handling mates
        const centipawns = [15]; // Initial position
        for (const evaluation of evaluations) {
            if (typeof evaluation === "string" && evaluation.startsWith("#")) {
                const mateIn = parseInt(evaluation.substring(1));
                centipawns.push(mateIn > 0 ? MATE_EQUIVALENT : -MATE_EQUIVALENT);
            }
            else {
                centipawns.push(evaluation);
            }
        }
        // Convert all to win percentages
        const allWinPercents = centipawns.map((cp) => centipawnsToWinPercent(cp));
        // Calculate window size and volatilities
        const windowSize = Math.max(2, Math.min(8, Math.floor(centipawns.slice(1).length / 10)));
        const volatilities = calculateVolatilitiesFromWinPercents(allWinPercents, windowSize);
        // Calculate weighted accuracies for each move
        const weightedAccuracies = [];
        for (let i = 0; i < allWinPercents.length - 1; i++) {
            const prev = allWinPercents[i];
            const next = allWinPercents[i + 1];
            const weight = volatilities[Math.min(i, volatilities.length - 1)];
            const isWhiteMove = i % 2 == 0;
            let winPercentBefore, winPercentAfter;
            if (isWhiteMove) {
                winPercentBefore = prev;
                winPercentAfter = next;
            }
            else {
                winPercentBefore = next;
                winPercentAfter = prev;
            }
            const accuracy = calculateMoveAccuracy(winPercentBefore, winPercentAfter);
            weightedAccuracies.push({
                accuracy: accuracy,
                weight: weight,
                color: isWhiteMove ? "White" : "Black",
            });
        }
        // Separate by color and calculate game accuracies
        const whiteAccuracies = weightedAccuracies.filter((wa) => wa.color === "White");
        const blackAccuracies = weightedAccuracies.filter((wa) => wa.color === "Black");
        // Calculate final accuracies
        const whiteGameAccuracy = calculateColorAccuracy(whiteAccuracies);
        const blackGameAccuracy = calculateColorAccuracy(blackAccuracies);
        return {
            player1Accuracy: Math.round(Math.max(0, Math.min(100, whiteGameAccuracy)) * 10) / 10,
            player2Accuracy: Math.round(Math.max(0, Math.min(100, blackGameAccuracy)) * 10) / 10,
        };
    }
    catch (error) {
        return {
            player1Accuracy: 0,
            player2Accuracy: 0,
        };
    }
}
